import { useTreeViewOutletContext } from "@/components/layout/TreeViewOutletContext";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  listRepoCommits,
  PaperRepoRequestError,
  patchRepoPaperEntry,
  type PaperRepoPaperPatchPayload,
  type RepoPaperRow,
} from "@/features/repos/api/paperRepos";
import { DetailField } from "@/features/repos/components/DetailField";
import { repoCommitsQueryKey } from "@/features/repos/repoQueries";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";

const mergeRepoPaperAfterPatch = (
  prev: RepoPaperRow,
  payload: PaperRepoPaperPatchPayload,
): RepoPaperRow => {
  let repo_note = prev.repo_note;
  const annotsRaw = prev.annotations;
  let annotations =
    annotsRaw && Array.isArray(annotsRaw) ? [...annotsRaw] : [];

  if ("repo_note" in payload && payload.repo_note !== undefined) {
    repo_note = payload.repo_note;
  }
  if (payload.append_annotation) {
    const ann = payload.append_annotation;
    annotations = [...annotations, { note: ann.note, lines: ann.lines ?? undefined }];
    if (payload.repo_note === undefined) {
      repo_note = ann.note;
    }
  }
  return { ...prev, repo_note, annotations };
}

export const PaperDetailPanel = ({ onClose }: { onClose: () => void }) => {
  const { selectedPaper, setSelectedPaper, commitsRepoId } = useTreeViewOutletContext();
  const queryClient = useQueryClient();
  const [abstractOpen, setAbstractOpen] = useState(false);
  const [repoNoteDraft, setRepoNoteDraft] = useState("");
  const [appendNote, setAppendNote] = useState("");
  const [appendLines, setAppendLines] = useState("");

  const branchForCommits = selectedPaper?.branch ?? "";

  const { data: commitsData, isLoading: commitsLoading } = useQuery({
    queryKey:
      commitsRepoId && selectedPaper ?
        repoCommitsQueryKey(commitsRepoId, {
          page: 1,
          per_page: 100,
          branch: branchForCommits,
        })
        : ["paper-repos", "commits", "disabled"],
    queryFn: () =>
      listRepoCommits(commitsRepoId!, {
        page: 1,
        per_page: 100,
        branch: branchForCommits || undefined,
      }),
    enabled: !!commitsRepoId && !!selectedPaper,
  });

  const patchEntryMutation = useMutation({
    mutationFn: async (payload: PaperRepoPaperPatchPayload) => {
      if (!commitsRepoId || !selectedPaper) {
        throw new PaperRepoRequestError("Cannot update entry", 400);
      }
      return patchRepoPaperEntry(commitsRepoId, selectedPaper.id, payload, selectedPaper.branch ?? null);
    },
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({ queryKey: ["paper-repos"] });
      setSelectedPaper((prev) =>
        !prev ? prev : mergeRepoPaperAfterPatch(prev, payload),
      );
      setAppendNote("");
      setAppendLines("");
    },
    onError: (err) =>
      alert(err instanceof PaperRepoRequestError ? err.message : "Could not save repository entry."),
  });

  useEffect(() => {
    if (!commitsRepoId || !selectedPaper) {
      setRepoNoteDraft("");
      setAppendNote("");
      setAppendLines("");
    } else {
      setRepoNoteDraft(selectedPaper.repo_note ?? "");
      setAppendNote("");
      setAppendLines("");
    }
  }, [commitsRepoId, selectedPaper]);

  if (!selectedPaper) {
    return null;
  }

  const paper = selectedPaper;

  return (
    <div className="flex h-full w-full flex-col bg-white">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-zinc-100 px-4 py-3">
        <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">Details</p>
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0"
          onClick={onClose}
          aria-label="Close details"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
        <div>
          <p className="text-sm font-semibold leading-snug text-zinc-900">{paper.title}</p>
          {paper.branch && (
            <p className="mt-1 text-xs text-zinc-400">Branch: {paper.branch}</p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 text-sm">
          {paper.authors && <DetailField label="Authors" value={paper.authors} />}
          {paper.doi && (
            <DetailField label="DOI" value={paper.doi} href={`https://doi.org/${paper.doi}`} />
          )}
          {paper.pmid && (
            <DetailField
              label="PMID"
              value={paper.pmid}
              href={`https://pubmed.ncbi.nlm.nih.gov/${paper.pmid}`}
            />
          )}
          {paper.pmc_id && <DetailField label="PMC" value={paper.pmc_id} />}
          {paper.volume && (
            <DetailField
              label="Volume / Issue"
              value={`${paper.volume}${paper.issue ? ` (${paper.issue})` : ""}`}
            />
          )}
          {paper.pages && <DetailField label="Pages" value={paper.pages} />}
          {paper.entry_type && <DetailField label="Type" value={paper.entry_type} />}
          {paper.source_key && <DetailField label="Cite key" value={paper.source_key} breakWord />}
          {paper.cited_by_count != null && (
            <DetailField
              label="Citations (approx.)"
              value={Intl.NumberFormat("en-US").format(paper.cited_by_count)}
            />
          )}
        </div>

        {paper.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {paper.tags.map((tag) => (
              <Badge key={tag}>{tag}</Badge>
            ))}
          </div>
        )}

        {paper.abstract && (
          <div>
            <p
              className={`text-sm leading-relaxed text-zinc-500 ${abstractOpen ? "" : "line-clamp-6"
                }`}
            >
              {paper.abstract}
            </p>
            {paper.abstract.length > 320 && (
              <button
                type="button"
                className="mt-1 text-xs text-zinc-500 hover:text-zinc-800"
                onClick={() => setAbstractOpen((v) => !v)}
              >
                {abstractOpen ? "Show less" : "Expand"}
              </button>
            )}
          </div>
        )}

        {commitsRepoId && (
          <div className="border-t border-zinc-100 pt-4">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">
              Commit history (branch: {paper.branch})
            </p>
            {commitsLoading && (
              <div className="flex items-center gap-2 py-2 text-xs text-zinc-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                Loading…
              </div>
            )}
            {!commitsLoading && commitsData != null && commitsData.commits.length === 0 && (
              <p className="text-xs text-zinc-500">
                No commits yet. Use <strong>Commit</strong> in the repo toolbar to snapshot this branch, or open{" "}
                <strong>Log</strong> for the full list.
              </p>
            )}
            {!commitsLoading && commitsData != null && commitsData.commits.length > 0 && (
              <ul className="max-h-40 space-y-2 overflow-y-auto text-xs text-zinc-600">
                {commitsData.commits.slice(0, 8).map((c) => (
                  <li key={`${c.sha}-${String(c.committed_at)}`} className="border-b border-zinc-50 pb-2 last:border-0">
                    <span className="font-mono text-amber-800">{c.sha.slice(0, 8)}</span>
                    {c.message != null && c.message !== "" && (
                      <span className="mt-0.5 block text-zinc-800">{c.message}</span>
                    )}
                    <span className="text-zinc-400">
                      {new Date(c.committed_at).toLocaleString()} · {c.paper_count} papers
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {commitsData != null && commitsData.commits.length > 8 && (
              <p className="mt-1 text-2xs text-zinc-400">
                Showing 8 most recent — open Log on the repo page for the full list.
              </p>
            )}
          </div>
        )}

        {commitsRepoId && (
          <div className="border-t border-zinc-100 pt-4 space-y-3">
            <p className="text-xs font-medium uppercase tracking-wide text-zinc-500">Repository note</p>
            <textarea
              className="w-full min-h-[5rem] rounded-[3px] border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300"
              value={repoNoteDraft}
              onChange={(e) => setRepoNoteDraft(e.target.value)}
              placeholder="Note for this paper in this repository…"
              disabled={patchEntryMutation.isPending}
            />
            <Button
              type="button"
              size="sm"
              className="w-full sm:w-auto"
              disabled={
                patchEntryMutation.isPending ||
                repoNoteDraft.trim() === (paper.repo_note ?? "").trim()
              }
              onClick={() =>
                patchEntryMutation.mutate({ repo_note: repoNoteDraft.trim() })
              }
            >
              {patchEntryMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…
                </>
              ) : (
                "Save repo note"
              )}
            </Button>

            <p className="text-xs font-medium uppercase tracking-wide text-zinc-500 pt-2">Append annotation</p>
            <Input
              value={appendNote}
              onChange={(e) => setAppendNote(e.target.value)}
              placeholder="Annotation text"
              disabled={patchEntryMutation.isPending}
            />
            <Input
              value={appendLines}
              onChange={(e) => setAppendLines(e.target.value)}
              placeholder="Optional line refs (e.g. L120–124)"
              className="font-mono text-xs"
              disabled={patchEntryMutation.isPending}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="w-full sm:w-auto"
              disabled={
                patchEntryMutation.isPending ||
                !(appendNote ?? "").trim()
              }
              onClick={() => {
                const note = appendNote.trim();
                const lines = appendLines.trim();
                patchEntryMutation.mutate({
                  append_annotation: {
                    note,
                    ...(lines ? { lines } : {}),
                  },
                });
              }}
            >
              {patchEntryMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Appending…
                </>
              ) : (
                "Append annotation"
              )}
            </Button>

            {paper.annotations &&
              Array.isArray(paper.annotations) &&
              paper.annotations.length > 0 && (
                <ul className="mt-3 space-y-2 text-xs text-zinc-600">
                  {paper.annotations.map((entry, idx) => {
                    const isNoteObj =
                      typeof entry === "object" &&
                      entry !== null &&
                      "note" in entry &&
                      typeof (entry as { note?: unknown }).note === "string";
                    if (isNoteObj) {
                      const annot = entry as { note: string; lines?: unknown };
                      const ln = annot.lines;
                      const linesStr = ln == null ? "" : String(ln).trim();
                      return (
                        <li
                          key={`a-${idx}`}
                          className="rounded border border-zinc-100 bg-zinc-50 px-2 py-1.5"
                        >
                          <span className="text-zinc-800">{annot.note}</span>
                          {linesStr.length > 0 && (
                            <span className="mt-0.5 block font-mono text-2xs text-zinc-400">
                              {linesStr}
                            </span>
                          )}
                        </li>
                      );
                    }
                    return (
                      <li key={`a-${idx}`} className="font-mono text-2xs text-zinc-500">
                        {JSON.stringify(entry)}
                      </li>
                    );
                  })}
                </ul>
              )}
          </div>
        )}

      </div>
    </div>
  );
}
